import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import MarcaDePlan from '../experiencia/MarcaDePlan';
import Ayuda from '../ui/Ayuda';
import { moduloDePantalla, modulosDeLaCuenta } from './modulosDeLaCuenta';
// Peaje U-04 (parche al carril escala): cerrado por falta de pago.
import CartelDeMora from '../peaje/CartelDeMora';
import { cerradoPorPago, estadoDeLaEscalera } from '../peaje/suscripcion';

/* =====================================================================
   El cartel de un módulo apagado · COBRO-CLIP B2 + R-PLAN2 (L1-3)
   =====================================================================
   Tres frases, en neutro, y lo que hace falta para SEGUIR, no un muro:
   · CORONA: «Está incluido en el plan X, desde $Y al mes» con las frases
     del catálogo (`incluye`), y el botón «Ver el plan X».
   · DIAMANTE: «Se cotiza aparte», y el botón para pedir la cotización.
   · En los dos: «¿Solo quieres este módulo? Cotízalo con nosotros», que
     deja la petición escrita y viaja a tu proveedor en el siguiente
     latido (constancia real en OPS).
   Nombres, precios y frases salen del catálogo que bajó por el latido —
   aquí no hay ni un precio escrito—. Sin catálogo, el cartel de siempre.
   Lo que ya existe se sigue viendo: el cartel no tapa nada. Lo monta
   `AppShell.jsx`; solo un administrador tiene la lectura de módulos.
   ===================================================================== */
const pesos = (n, moneda = 'MXN') => {
  try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda, maximumFractionDigits: 0 }).format(n); }
  catch { return `$${n}`; }
};

export default function CartelDeModulo({ pantalla, onIr }) {
  const { tieneNivel } = useAuth();
  const modulo = moduloDePantalla(pantalla);
  const [datos, setDatos] = useState(null);
  const [escalera, setEscalera] = useState(undefined); // undefined = leyendo
  const [pedida, setPedida] = useState(null);     // texto de confirmación
  const [pidiendo, setPidiendo] = useState(false);
  const [error, setError] = useState(null);
  const puedeMirar = Boolean(modulo) && Boolean(tieneNivel?.('ADMIN'));
  useEffect(() => {
    if (!puedeMirar) return undefined;
    let vivo = true;
    modulosDeLaCuenta(api).then((d) => { if (vivo) setDatos(d); });
    estadoDeLaEscalera(api).then((e) => { if (vivo) setEscalera(e); });
    return () => { vivo = false; };
  }, [puedeMirar]);
  if (!puedeMirar || !datos || escalera === undefined) return null;
  const fila = (datos.modulos || []).find((m) => m.modulo === modulo);
  if (!fila || !fila.en_consulta) return null;
  // DISEÑO-F1b D12 (parche del carril terminal): la pantalla donde se
  // conecta una terminal comprada no lleva muro; el servidor dice cuáles.
  if (Array.isArray(fila.sin_muro_en) && fila.sin_muro_en.includes(pantalla)) return null;
  // Peaje U-04: la misma regla que el 403 con `motivo: "pago"`.
  if (cerradoPorPago(fila, escalera)) {
    return <CartelDeMora nombre={fila.nombre} desde={escalera.desde} onIr={onIr} />;
  }
  const marca = fila.marca || 'candado';
  const plan = fila.plan;

  async function cotizar() {
    setPidiendo(true); setError(null);
    try {
      const r = await api.post('/escala/suscripcion/cotizar', { modulo });
      setPedida(r.mensaje);
    } catch (e) {
      setError(e.message || 'No se pudo pedir la cotización. Vuelve a intentarlo.');
    } finally {
      setPidiendo(false);
    }
  }

  return (
    <div role="status" className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 md:mx-6">
      <div className="flex flex-wrap items-start gap-3">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="flex-1 space-y-1">
          <p>
            <strong>{fila.nombre}</strong> está <strong>en consulta</strong> en tu cuenta: se ve todo lo
            que ya existe, pero no se registra nada nuevo. <MarcaDePlan marca={marca} />
          </p>
          {marca === 'corona' && plan && (
            <p>
              Está incluido en el plan <strong>{plan.nombre}</strong>
              {plan.precio_mensual > 0 ? <> desde <strong>{pesos(plan.precio_mensual, plan.moneda)}</strong> al mes</> : null}.
              {Array.isArray(plan.incluye) && plan.incluye.length > 0 && (
                <span className="block text-[13px] text-amber-800">{plan.incluye.join(' · ')}</span>
              )}
            </p>
          )}
          {marca === 'diamante' && (
            <p>Este módulo no viene en ningún plan: <strong>se cotiza aparte</strong>, a la medida de tu empresa.</p>
          )}
          {marca === 'candado' && (
            <p>El módulo no está encendido para esta cuenta. Para pedirlo, ve a Suscripción.</p>
          )}
          <p className="text-[13px]">¿Solo quieres este módulo? Cotízalo con nosotros.</p>
          <Ayuda id="escala.modulo_cerrado" />
          {pedida && <p className="rounded-lg bg-white/70 p-2 text-emerald-800" role="status">{pedida}</p>}
          {error && <p className="rounded-lg bg-white/70 p-2 text-red-800" role="alert">{error}</p>}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 pl-7">
        {marca === 'corona' && plan && onIr && (
          <button type="button" onClick={() => onIr('suscripcion')}
                  className="rounded-lg bg-[var(--color-marca)] px-3 py-1.5 font-semibold text-white">
            Ver el plan {plan.nombre}
          </button>
        )}
        {marca === 'candado' && onIr && (
          <button type="button" onClick={() => onIr('suscripcion')}
                  className="rounded-lg bg-[var(--color-marca)] px-3 py-1.5 font-semibold text-white">
            Pedir este módulo
          </button>
        )}
        {!pedida && (
          <button type="button" onClick={cotizar} disabled={pidiendo}
                  className={`rounded-lg px-3 py-1.5 font-semibold ${marca === 'diamante'
                    ? 'bg-[var(--color-marca)] text-white' : 'border border-amber-300 bg-white text-amber-900'} disabled:opacity-60`}>
            {pidiendo ? 'Pidiendo…' : 'Pedir cotización'}
          </button>
        )}
      </div>
    </div>
  );
}
